/**
 * One timeline track row. Reveals hold a single appear-and-stay clip (no end);
 * effect kinds (annotation / pen / spotlight) hold one clip WITH a resizable
 * out-time; the Camera track holds many blocks on one row. Drag a clip's body to
 * re-time it (snaps to words + the seekbar); drag its right grip to set the
 * out-time. An element with no reveal yet shows an empty, clickable row.
 */

import { Link } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { ItemKind, TimelineClip, TimelineTrack } from './items';
import { useItemDrag } from './useItemDrag';

export const ROW_H = 26;

interface Props {
  track: TimelineTrack;
  pxPerMs: number;
  total: number;
  rowWidth: number;
  wordStarts: number[];
  playheadMs: number;
  selectedRefId: string | null;
  onSelect: (clip: TimelineClip) => void;
  onMove: (clip: TimelineClip, globalStart: number) => void;
  onResize: (clip: TimelineClip, globalEnd: number) => void;
  onAddAt: (globalMs: number) => void;
  onMenu: (clip: TimelineClip, x: number, y: number) => void;
}

export function TrackRow({
  track,
  pxPerMs,
  total,
  rowWidth,
  wordStarts,
  playheadMs,
  selectedRefId,
  onSelect,
  onMove,
  onResize,
  onAddAt,
  onMenu,
}: Props) {
  const empty = track.clips.length === 0;
  return (
    <div
      className="relative border-b border-border-subtle/70"
      style={{ height: ROW_H, width: rowWidth }}
      onPointerDown={(e) => {
        if (empty && track.emptyElementId && e.button === 0 && e.currentTarget === e.target) {
          const rect = e.currentTarget.getBoundingClientRect();
          onAddAt(Math.max(0, Math.min(total, (e.clientX - rect.left) / pxPerMs)));
        }
      }}
    >
      {empty && track.emptyElementId && (
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[10px] italic text-fg-subtle">
          click to reveal…
        </span>
      )}
      {track.clips.map((clip) => (
        <ClipBlock
          key={clip.refId}
          clip={clip}
          kind={track.kind}
          pxPerMs={pxPerMs}
          total={total}
          rowWidth={rowWidth}
          wordStarts={wordStarts}
          playheadMs={playheadMs}
          selected={selectedRefId === clip.refId}
          onSelect={() => onSelect(clip)}
          onMove={(g) => onMove(clip, g)}
          onResize={(g) => onResize(clip, g)}
          onMenu={(x, y) => onMenu(clip, x, y)}
        />
      ))}
    </div>
  );
}

function ClipBlock({
  clip,
  kind,
  pxPerMs,
  total,
  rowWidth,
  wordStarts,
  playheadMs,
  selected,
  onSelect,
  onMove,
  onResize,
  onMenu,
}: {
  clip: TimelineClip;
  kind: ItemKind;
  pxPerMs: number;
  total: number;
  rowWidth: number;
  wordStarts: number[];
  playheadMs: number;
  selected: boolean;
  onSelect: () => void;
  onMove: (g: number) => void;
  onResize: (g: number) => void;
  onMenu: (x: number, y: number) => void;
}) {
  const { preview, dragging, onBodyDown, onResizeDown } = useItemDrag({
    pxPerMs,
    total,
    startMs: clip.startMs,
    endMs: clip.endMs,
    wordStarts,
    playheadMs,
    onMove,
    onResize,
  });

  const startMs = preview ? preview.startMs : clip.startMs;
  const endMs = preview ? preview.endMs : clip.endMs;
  const openEnd = endMs == null; // reveals or effects without an out-time set yet

  const left = Math.round(startMs * pxPerMs);
  const width = openEnd
    ? Math.max(10, rowWidth - left)
    : Math.max(10, Math.round((endMs - startMs) * pxPerMs));
  const resizable = kind !== 'reveal';

  return (
    <div
      role="button"
      tabIndex={0}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        onSelect();
        onBodyDown(e);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onSelect();
        onMenu(e.clientX, e.clientY);
      }}
      className={cn(
        'group/clip absolute top-1 bottom-1 flex cursor-grab items-center gap-1 overflow-hidden rounded-[5px] border pl-2 pr-2 text-[10px] font-medium leading-none text-white/95 select-none active:cursor-grabbing',
        selected ? 'ring-2 ring-fg ring-offset-1' : '',
        dragging && 'opacity-90',
        openEnd && kind === 'reveal' && 'rounded-r-none',
      )}
      style={{
        left,
        width,
        background: openEnd
          ? `linear-gradient(90deg, ${clip.color} 0%, ${clip.color} 64px, ${clip.color}66 100%)`
          : clip.color,
        borderColor: 'rgba(0,0,0,0.25)',
      }}
      title={clip.label}
    >
      <span className="absolute left-0 top-0 h-full w-1 bg-black/25" />
      {clip.bound && <Link size={9} className="shrink-0 opacity-90" />}
      <span className="truncate">{clip.label}</span>
      {resizable && (
        <span
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            onSelect();
            onResizeDown(e);
          }}
          className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize bg-black/20 opacity-0 group-hover/clip:opacity-100"
          title="Drag to set the out-time"
        />
      )}
    </div>
  );
}
